@file:OptIn(ExperimentalSerializationApi::class)

package ui.resource.icons

import AppBuildConfig
import androidx.compose.runtime.*
import androidx.compose.ui.text.input.TextFieldValue
import io.Repository
import io.ktor.client.plugins.websocket.*
import io.ktor.http.*
import io.ktor.utils.io.core.*
import io.ktor.websocket.*
import kotlinx.coroutines.*
import kotlinx.coroutines.channels.consumeEach
import kotlinx.serialization.ExperimentalSerializationApi
import org.ssoft.common.server.icons.IconIO
import org.ssoft.server.boundary.icons.BoundaryIcons
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState
import tk.mallumo.utils.mutableDataState
import kotlin.time.Duration.Companion.seconds

@VM
class IconsListingVM : NavigationViewModel() {

    private var session: DefaultClientWebSocketSession? = null
    private var job: Job? = null
    private var sessionActive = false

    private lateinit var scopeUi: IconsListingScope

    val searchFiled = mutableStateOf(TextFieldValue(""))

    var state by mutableDataState<IconsListingScope.Wrapper>()
        private set


    sealed interface Action {
        data object Random : Action
        data object Query : Action
        data object LoadMore : Action
        companion object {

            @Composable
            fun rememberAction(vm: IconsListingVM): (Action) -> Unit {
                return remember {
                    { act ->
                        vm.scope.launch {
                            when (act) {
                                Query -> vm.callSearch()
                                Random -> {
                                    vm.searchFiled.value = TextFieldValue()
                                    vm.callRandom()
                                }

                                LoadMore -> {
                                    if (vm.searchFiled.value.text.trim().length > 1) {
                                        vm.callSearchMore()
                                    } else {
                                        vm.callRandomMore()
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    fun init(scopeUi: IconsListingScope) {
        this.scopeUi = scopeUi
        if (state !is DataState.Result) {
            state = DataState.Loading()
        }

        scope.launch {
            runCatching {
                val result = Repository.api.icons.random()
                state = DataState.Result(IconsListingScope.Wrapper(result))
            }.onFailure {
                it.printStackTrace()
                state = DataState.Error(it.message!!)
            }

        }
        scope.launch {

            connect {
                runCatching {
                    val result = decodeResult(it)
                    if (result.offset > 0) {
                        state.entry?.addAll(result.items)
                    } else {
                        state = DataState.Result(IconsListingScope.Wrapper(result))
                    }
                }.onFailure { it.printStackTrace() }
            }
        }
    }

    @ExperimentalSerializationApi
    private suspend fun callSearch() {
        searchFiled.value.text.trim().takeIf { it.length > 1 }
            ?.also { q ->
                with(BoundaryIcons) {
                    runCatching {
                        session?.sendRequest(IconIO.Query(q, offset = 0))
                            ?: error("not connected to icons server")

                    }.onFailure { it.printStackTrace() }
                }
            }
    }

    @ExperimentalSerializationApi
    private suspend fun callSearchMore() {
        val currentIds = state.entry?.let { it.state.map { it.data.id } } ?: listOf()
        searchFiled.value.text.trim()
            .takeIf { currentIds.isNotEmpty() }
            ?.takeIf { it.length > 2 }
            ?.also { q ->
                with(BoundaryIcons) {
                    runCatching {
                        session
                            ?.sendRequest(IconIO.Query(q, offset = currentIds.size))
                            ?: error("not connected to icons server")

                    }.onFailure { it.printStackTrace() }
                }
            }
    }

    @ExperimentalSerializationApi
    private suspend fun callRandomMore() {
        with(BoundaryIcons) {
            runCatching {
                val currentIds = state.entry?.let { it.state.map { it.data.id } } ?: listOf()
                session?.sendRequest(IconIO.Random(exclusion = currentIds))
                    ?: error("not connected to icons server")

            }.onFailure { it.printStackTrace() }
        }
    }

    @ExperimentalSerializationApi
    private suspend fun callRandom() {
        with(BoundaryIcons) {
            runCatching {
                session?.sendRequest(IconIO.Random())
                    ?: error("not connected to icons server")
            }.onFailure { it.printStackTrace() }
        }
    }


    @ExperimentalSerializationApi
    private suspend fun connect(handleMessage: BoundaryIcons.(ByteArray) -> Unit) = coroutineScope {

        disconnect()
        sessionActive = true
        job = launch {
            while (isActive && sessionActive) {
                runCatching {
                    Repository.api.client.ws().use {
                        it.wss(
                            method = HttpMethod.Get,
                            host = AppBuildConfig.HOST_ICONS,
                            port = AppBuildConfig.PORT_ICONS,
                            path = "/api/connection"
                        ) {
                            session = this
                            incoming.consumeEach { message ->
                                if (message is Frame.Binary) {
                                    with(BoundaryIcons) {
                                        handleMessage(message.readBytes())
                                    }
                                }
                            }
                        }
                    }
                }
                if (isActive && sessionActive) delay(1.seconds)
            }
        }

    }

    suspend fun disconnect() {
        sessionActive = false
        session = null
        job?.takeIf { it.isActive }
            ?.runCatching {
                cancel()
            }
    }


    override fun onRelease() {
        scope.launch {
            disconnect()
        }
        state.entry?.release()
        state = DataState.Idle()
    }

}
